import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Search, Box, ArrowRightLeft, Wallet, Loader2 } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { search } from '../lib/api';
import { Button } from '../components/ui/button';

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const performSearch = async () => {
      if (!query) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        const res = await search(query);
        setResult(res.data);
        
        // Auto-redirect based on result type
        if (res.data.type === 'block') {
          navigate(`/block/${res.data.result}`, { replace: true });
        } else if (res.data.type === 'transaction') {
          navigate(`/tx/${res.data.result}`, { replace: true });
        } else if (res.data.type === 'address') {
          navigate(`/address/${res.data.result}`, { replace: true });
        }
      } catch (err) {
        setError('No results found for your search query');
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [query, navigate]);

  const getIcon = (type) => {
    switch (type) {
      case 'block': return Box;
      case 'transaction': return ArrowRightLeft;
      case 'address': return Wallet;
      default: return Search;
    }
  };

  const getLink = (result) => {
    switch (result.type) {
      case 'block': return `/block/${result.result}`;
      case 'transaction': return `/tx/${result.result}`;
      case 'address': return `/address/${result.result}`;
      default: return '/';
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="search-page">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 rounded-xl bg-gradient-to-br from-[#00F0FF]/20 to-transparent">
            <Search className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Search Results</h1>
            <p className="text-sm text-muted-foreground">
              Query: {query || 'No query provided'}
            </p>
          </div>
        </div>

        <div className="data-card p-8">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-primary" />
              <p className="text-muted-foreground">Searching...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <Search className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-bold mb-2">No Results Found</h2>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Link to="/">
                <Button>Back to Home</Button>
              </Link>
            </div>
          ) : result ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center">
                {(() => {
                  const Icon = getIcon(result.type);
                  return <Icon className="w-8 h-8 text-primary" />;
                })()}
              </div>
              <h2 className="text-xl font-bold mb-2 capitalize">{result.type} Found</h2>
              <p className="text-muted-foreground mb-4">Redirecting...</p>
              <Link to={getLink(result)}>
                <Button>View {result.type}</Button>
              </Link>
            </div>
          ) : (
            <div className="text-center py-12">
              <Search className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-bold mb-2">Enter a Search Query</h2>
              <p className="text-muted-foreground">
                Search for blocks, transactions, or addresses
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
